<template>
	<div class="fixed left-0 top-0 z-[20] w-full">
		<nav class="z-20">
			<dropdown-menu
				class="showMobile"
				:offset="[-280, 0]"
				:no-opacity="true"
				close-on-action
				:anim-duration="300"
				:items="menuItems"
				:mobile-nav="true"
				:mobile="true"
				:show-version="true"
			>
				<template #image>
					<span class="fa-light fa-bars rounded-md p-2 text-[26px] hover:bg-gray-700"></span>
				</template>
				<template #logo>
					<div class="flex w-full flex-col px-6 py-4">
						<img src="/assets/images/teleflow-logo-colour.png" width="160" class="mobileLogo w-full" />
						<div v-if="shouldShowBalance" class="mb-2 mt-6">
							<div class="text-sm text-gray-400">Balance</div>
							<div class="text-2xl font-light">{{ $global.currencyGbp(balance) }}</div>
						</div>
					</div>
				</template>
			</dropdown-menu>

			<span
				class="fa-light fa-bars mr-2 hidden rounded-md p-2 text-[26px] transition-all hover:bg-gray-700 active:scale-90 sm:block"
				@click="toggleSidebar"
			>
			</span>

			<router-link class="brandLogo cursor-pointer" to="/dashboard">
				<img
					src="/assets/images/teleflow-logo-colour.png"
					class="h-6 sm:h-7"
					height="40"
					width="auto"
					alt="Teleflow Logo"
				/>
			</router-link>

			<div class="ml-8 hidden sm:block">
				<button
					class="flex cursor-pointer items-center gap-3 rounded-lg px-4 py-2 text-sm text-gray-400 transition-all hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-700 active:scale-90"
					@click="openSearch"
				>
					<span class="fa-light fa-magnifying-glass h-4 w-4"></span>
					<span class="text-gray-400 dark:text-gray-300">Search...</span>
					<kbd class="rounded bg-gray-700 px-2 py-1 text-xs text-gray-400">{{ modifierKey }} K</kbd>
				</button>
			</div>

			<div class="absolute right-4">
				<button
					id="theme-toggle"
					aria-label="Toggle Dark Mode"
					type="button"
					class="cursor-default rounded-lg p-2.5 text-sm text-gray-400 transition-all hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-700 active:scale-90"
					@click="toggleDarkMode"
				>
					<span
						id="theme-toggle-dark-icon"
						class="fa-solid fa-moon h-4 w-4"
						:class="{ hidden: darkMode }"
					></span>
					<span id="theme-toggle-light-icon" class="fa-solid fa-sun h-4 w-4" :class="{ hidden: !darkMode }">
					</span>
				</button>
			</div>

			<dropdown-menu
				:nav="true"
				class="dropdownMed absolute right-16"
				:items="[
					{
						handler: () => {
							showContactFormModal = true
						},
						icon: 'fa-light fa-question-circle',
						label: 'Contact Us'
					},
					{
						handler: logout,
						icon: 'fa-light fa-arrow-right-from-bracket',
						label: 'Logout'
					}
				]"
			>
				<template #label class="name">
					<div
						class="user-menu max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap font-medium transition-colors group-hover/tr:text-primary"
					>
						{{
							user?.given_name && user?.family_name
								? user?.given_name + ' ' + user?.family_name
								: user?.email
						}}
					</div>
				</template>
				<template #image>
					<div class="navAvatarNoInvert">
						<img
							class="peer/tr max-w-none"
							:src="user?.avatar ? avatarUrl + user?.avatar : '/assets/images/teleflow-icon.png'"
							width="40"
							height="20"
							alt="TeleFlow Icon"
						/>
					</div>
				</template>
				<template #icon
					><div
						class="navAvatarNoInvert fa-light fa-chevron-down transition-colors group-hover/tr:text-primary"
					></div
				></template>
			</dropdown-menu>
		</nav>
	</div>
</template>

<script>
import { router } from '../router'
import { getFilteredNavigation } from '../config/navigation'

export default {
	inject: [
		'user',
		'userRoles',
		'showContactFormModal',
		'modalProp1',
		'modalProp2',
		'tenants',
		'userAccounts',
		'sidebarOpen'
	],
	data() {
		return {
			globalSearchTerm: '',
			darkMode: localStorage.getItem('darkMode')
				? localStorage.getItem('darkMode') === 'true'
				: window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches,
			isMac: navigator.platform.toUpperCase().indexOf('MAC') >= 0
		}
	},
	computed: {
		modifierKey() {
			return this.isMac ? '⌘' : 'Ctrl'
		},
		shouldShowBalance() {
			return (
				this.userRoles.includes('account') &&
				this.userAccounts?.find((account) => account.billingType === 'PRE_PAID')
			)
		},
		balance() {
			if (!this.shouldShowBalance) return 0
			return this.userAccounts?.reduce((acc, account) => acc + parseFloat(account.balanceGbp), 0) || 0
		},
		menuItems() {
			const navItems = getFilteredNavigation(this.userRoles, this.$global).map((item) => ({
				href: item.path,
				icon: `fa-light ${item.iconClass}`,
				label: item.title,
				role: item.role,
				new: item.new,
				beta: item.beta
			}))

			return [
				...navItems,
				{
					handler: () => {
						this.showContactFormModal = true
					},
					icon: 'fa-light fa-question',
					label: 'Contact Us'
				},
				{
					handler: this.logout,
					icon: 'fa-light fa-arrow-right-from-bracket',
					label: 'Logout'
				}
			]
		}
	},
	methods: {
		searchTenants() {
			if (this.globalSearchTerm) router.push({ path: 'movers', query: { globalSearch: this.globalSearchTerm } })

			this.globalSearchTerm = ''
		},
		toggleSidebar() {
			this.sidebarOpen = !this.sidebarOpen
			localStorage.setItem('sidebarOpen', this.sidebarOpen ? 'true' : 'false')
		},
		logout() {
			this.$auth0.logout({ logoutParams: { returnTo: window.location.origin } })
		},
		routeTo(routeName) {
			router.push(routeName)
		},
		toggleDarkMode() {
			const darkMode = localStorage.getItem('darkMode')
			localStorage.setItem('darkMode', darkMode === 'true' ? 'false' : 'true')
			this.darkMode = !this.darkMode
			window.setDarkMode()
		},
		openSearch() {
			const event = new KeyboardEvent('keydown', {
				key: 'k',
				metaKey: true,
				ctrlKey: true
			})
			window.dispatchEvent(event)
		}
	}
}
</script>
